import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Truck, FileText, Receipt, Smartphone, CheckCircle2 } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#002FA7] grid place-items-center text-white">
              <Truck className="w-5 h-5" strokeWidth={2.4} />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "Outfit" }}>
              Fleet Sheet
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/sign-in">
              <Button variant="ghost" data-testid="nav-sign-in-btn">Sign in</Button>
            </Link>
            <Link to="/sign-up">
              <Button data-testid="nav-sign-up-btn" className="bg-[#002FA7] hover:bg-[#001A75]">
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="pill bg-blue-50 text-[#002FA7] border border-blue-200">
            For small trucking carriers
          </span>
          <h1
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight text-slate-900"
            style={{ fontFamily: "Outfit" }}
          >
            Every load, one folder.
            <br />
            <span className="text-[#002FA7]">Get paid faster.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-lg">
            Fleet Sheet keeps your ratecon, driver PODs, and invoice together per load — so nothing
            slips through the cracks and billing takes minutes, not hours.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <Link to="/sign-up">
              <Button size="lg" className="bg-[#002FA7] hover:bg-[#001A75] rounded-full px-8 h-12" data-testid="hero-cta-btn">
                Start free
              </Button>
            </Link>
            <Link to="/sign-in">
              <Button size="lg" variant="outline" className="rounded-full px-6 h-12" data-testid="hero-signin-btn">
                I already have an account
              </Button>
            </Link>
          </div>
          <ul className="mt-8 space-y-2 text-sm text-slate-600">
            {[
              "Driver uploads POD from their phone — load auto-marked Delivered",
              "One-click invoice with sequential numbers, printable PDF",
              "Export the full packet (ratecon + POD + invoice) as one PDF",
            ].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-indigo-50 rounded-3xl blur-xl opacity-70" />
          <div className="relative rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1761133381018-aed5063d22fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Truck on highway"
              className="w-full h-72 object-cover"
            />
            <div className="p-5 grid grid-cols-3 gap-3 bg-white">
              {[
                { icon: FileText, label: "Ratecon" },
                { icon: Smartphone, label: "POD" },
                { icon: Receipt, label: "Invoice" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="rounded-xl border border-slate-200 p-3 text-center">
                  <Icon className="w-5 h-5 mx-auto text-[#002FA7]" />
                  <div className="mt-1 text-xs font-medium text-slate-700">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-6">
          {[
            {
              title: "Dispatch creates the load",
              body: "Load number, broker, rate, pickup/delivery, assign a driver, attach the ratecon.",
            },
            {
              title: "Driver snaps the POD",
              body: "Mobile-first. Tap the load, use the camera. Status flips to Delivered automatically.",
            },
            {
              title: "Billing in one click",
              body: "Auto-filled invoice, sequential numbering, printable PDF, downloadable combined packet.",
            },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="text-sm font-semibold text-[#002FA7]">Workflow</div>
              <div className="mt-2 text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
                {f.title}
              </div>
              <p className="mt-2 text-sm text-slate-600">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10 text-center text-sm text-slate-500">
        Fleet Sheet — simple load paperwork and invoicing for small carriers.
      </footer>
    </div>
  );
}
